import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    Quarter,
    Recurrence,
    ControlsPageFiltersForControlsQuarterlyPageSpecificFilters as ControlsQuarterlyFilters,
    ControlsQuarterlyPageSpecificFilters,
    ControlsQuarterlyColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsQuarterlyColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    recurrenceQuarterly: null,
    notification: null,
    scheduleQuarterly: null,
    assignment: null,
    tags: null,
    accessRight: null,
    status: null,
    loginProtection: null,
    segregationOfDuties: null,
    financialYear: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsQuarterlyColumn[] = Object.keys(
    allColumnsGuard
) as ControlsQuarterlyColumn[];
const encodeControlsQuarterlyColumn: (col: string) => ControlsQuarterlyColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsQuarterlySpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsQuarterlyPageSpecificFilters {
    return {
        notification: encodeOrFilter<number>("notification", filters),
        financialYear: encodeOrFilter<number>("financialYear", filters),
        recurrenceQuarterly: encodeOrFilter<Recurrence>(
            "recurrenceQuarterly",
            filters
        ),
        scheduleQuarterly: encodeOrFilter<Quarter[]>(
            "scheduleQuarterly",
            filters
        ),
    };
}
const encodeControlsQuarterlyPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsQuarterlyFilters = createEncodePageFiltersFn(
    encodeControlsQuarterlySpecificPageFilters
);

function decodeControlsQuarterlyPageSpecificFilters(
    specificFilters: ControlsQuarterlyPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.notification) {
        output.push(
            decodeOrFilter<number>("notification", specificFilters.notification)
        );
    }
    if (specificFilters.financialYear) {
        output.push(
            decodeOrFilter<number>(
                "financialYear",
                specificFilters.financialYear
            )
        );
    }
    if (specificFilters.recurrenceQuarterly) {
        output.push(
            decodeOrFilter<Recurrence>(
                "recurrenceQuarterly",
                specificFilters.recurrenceQuarterly
            )
        );
    }
    if (specificFilters.scheduleQuarterly) {
        output.push(
            decodeOrFilter<Quarter[]>(
                "scheduleQuarterly",
                specificFilters.scheduleQuarterly
            )
        );
    }
    return output;
}
const decodeControlsQuarterlyPageFilters: (
    pageFilters: ControlsQuarterlyFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsQuarterlyPageSpecificFilters
);

// Page state
//
type ControlsQuarterlyPageStateWithType = PageStateData<"ControlsQuarterly"> & {
    type: "ControlsQuarterly";
};

function decodeControlsQuarterlyPageState(
    pageState: ControlsQuarterlyPageStateWithType
): PageFilterAndColumns {
    const quarterlylPageState = unwrapPageStateAs(
        pageState,
        "ControlsQuarterly"
    );
    const filter: SimpleAndFilter = {
        filters: decodeControlsQuarterlyPageFilters(
            quarterlylPageState.filters
        ),
    };
    const visibleColumnIds = new Set(quarterlylPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsQuarterlyPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsQuarterlyPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsQuarterly",
        filters: encodeControlsQuarterlyPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(
            encodeControlsQuarterlyColumn
        ),
        version: filterAndColumns.version,
    };
}

export { encodeControlsQuarterlyPageState, decodeControlsQuarterlyPageState };

export type { ControlsQuarterlyPageStateWithType };
